/**
 * Typography utility functions for responsive scaling and accessibility
 */

import { Dimensions, AccessibilityInfo } from 'react-native';
import {
  typographyTokens,
  FontSize,
  TypographyVariant,
  AccessibilityTextScale,
} from '../tokens/typography';

// Device type detection
export type DeviceType = 'mobile' | 'tablet' | 'desktop';

// Additional types for the missing functions
export interface TypographyMetrics {
  fontSize: number;
  lineHeight: number;
  letterSpacing: number;
  fontWeight: string;
  fontFamily: string;
}

export interface ResponsiveFontConfig {
  fontSize: FontSize;
  deviceType?: DeviceType;
  accessibilityScale?: AccessibilityTextScale;
}

export interface TypographyValidation {
  isValid: boolean;
  issues: string[];
  recommendations: string[];
}

/**
 * Get device type based on screen dimensions
 */
export function getDeviceType(): DeviceType {
  const { width } = Dimensions.get('window');

  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

/**
 * Calculate responsive font size based on device type and accessibility settings
 */
export function getResponsiveFontSize(
  fontSize: FontSize,
  deviceType: DeviceType = getDeviceType(),
  accessibilityScale: AccessibilityTextScale = 'base'
): number {
  const baseSize = typographyTokens.fontSize[fontSize].size;
  const deviceScale =
    typographyTokens.scaling[deviceType][
      fontSize as keyof (typeof typographyTokens.scaling)[typeof deviceType]
    ];
  const accessibilityMultiplier =
    typographyTokens.accessibility.textScale[accessibilityScale];

  return Math.round(baseSize * deviceScale * accessibilityMultiplier);
}

/**
 * Calculate responsive line height
 */
export function getResponsiveLineHeight(
  fontSize: FontSize,
  deviceType: DeviceType = getDeviceType(),
  accessibilityScale: AccessibilityTextScale = 'base'
): number {
  const baseLineHeight = typographyTokens.fontSize[fontSize].lineHeight;
  const scaledFontSize = getResponsiveFontSize(
    fontSize,
    deviceType,
    accessibilityScale
  );

  // Maintain proportional line height
  const ratio = baseLineHeight / typographyTokens.fontSize[fontSize].size;
  return Math.round(scaledFontSize * ratio);
}

/**
 * Calculate optimal line height for a given font size
 */
export function getOptimalLineHeight(fontSize: number): number {
  // Optimal line height is typically 1.4-1.6 times the font size
  const optimalRatio = 1.5;
  return Math.round(fontSize * optimalRatio);
}

/**
 * Calculate optimal letter spacing for a given font size
 */
export function getOptimalLetterSpacing(fontSize: number): number {
  // Letter spacing is typically 0.5-2% of font size
  const optimalRatio = 0.01; // 1% of font size
  return Math.round(fontSize * optimalRatio);
}

/**
 * Calculate comprehensive text metrics for a given configuration
 */
export function calculateTextMetrics(
  config: ResponsiveFontConfig
): TypographyMetrics {
  const {
    fontSize,
    deviceType = getDeviceType(),
    accessibilityScale = 'base',
  } = config;

  const responsiveFontSize = getResponsiveFontSize(
    fontSize,
    deviceType,
    accessibilityScale
  );
  const responsiveLineHeight = getResponsiveLineHeight(
    fontSize,
    deviceType,
    accessibilityScale
  );
  const letterSpacing = getLetterSpacing(fontSize);
  const variantConfig =
    typographyTokens.variants[fontSize as TypographyVariant];

  return {
    fontSize: responsiveFontSize,
    lineHeight: responsiveLineHeight,
    letterSpacing,
    fontWeight:
      typographyTokens.fontWeight[variantConfig?.fontWeight || 'normal'],
    fontFamily: getFontFamilyString(variantConfig?.fontFamily || 'sans'),
  };
}

/**
 * Validate typography scale for consistency and accessibility
 */
export function validateTypographyScale(): TypographyValidation {
  const issues: string[] = [];
  const recommendations: string[] = [];

  // Check if all font sizes have proper line heights
  Object.entries(typographyTokens.fontSize).forEach(([size, config]) => {
    if (!config.lineHeight) {
      issues.push(`Missing line height for font size: ${size}`);
      recommendations.push(`Add lineHeight property to ${size} configuration`);
    }
  });

  // Check if all variants have required properties
  Object.entries(typographyTokens.variants).forEach(([variant, config]) => {
    if (!config.fontSize) {
      issues.push(`Missing fontSize for variant: ${variant}`);
      recommendations.push(`Add fontSize property to ${variant} configuration`);
    }
    if (!config.fontFamily) {
      issues.push(`Missing fontFamily for variant: ${variant}`);
      recommendations.push(
        `Add fontFamily property to ${variant} configuration`
      );
    }
  });

  // Check accessibility scaling values
  Object.entries(typographyTokens.accessibility.textScale).forEach(
    ([scale, value]) => {
      if (value < 0.8 || value > 2.0) {
        issues.push(`Accessibility scale ${scale} has extreme value: ${value}`);
        recommendations.push(
          `Consider adjusting ${scale} scale to be between 0.8 and 2.0`
        );
      }
    }
  );

  return {
    isValid: issues.length === 0,
    issues,
    recommendations,
  };
}

/**
 * Get letter spacing for font size
 */
export function getLetterSpacing(fontSize: FontSize): number {
  return typographyTokens.fontSize[fontSize].letterSpacing || 0;
}

/**
 * Get paragraph spacing for font size
 */
export function getParagraphSpacing(fontSize: FontSize): number {
  return typographyTokens.fontSize[fontSize].paragraphSpacing || 12;
}

/**
 * Get font family array as string
 */
export function getFontFamilyString(
  fontFamily: keyof typeof typographyTokens.fontFamily
): string {
  return typographyTokens.fontFamily[fontFamily].join(', ');
}

/**
 * Get complete typography style for a variant
 */
export function getTypographyStyle(
  variant: TypographyVariant,
  deviceType: DeviceType = getDeviceType(),
  accessibilityScale: AccessibilityTextScale = 'base'
) {
  const variantConfig = typographyTokens.variants[variant];
  const fontSize = variantConfig.fontSize as FontSize;

  return {
    fontFamily: getFontFamilyString(variantConfig.fontFamily || 'sans'),
    fontSize: getResponsiveFontSize(fontSize, deviceType, accessibilityScale),
    fontWeight: typographyTokens.fontWeight[variantConfig.fontWeight],
    letterSpacing: typographyTokens.letterSpacing[variantConfig.letterSpacing],
    lineHeight: getResponsiveLineHeight(
      fontSize,
      deviceType,
      accessibilityScale
    ),
  };
}

/**
 * Calculate optimal reading width based on font size
 * Following the 45-75 character rule for optimal readability
 */
export function getOptimalReadingWidth(fontSize: number): number {
  // Average character width is approximately 0.5-0.6 times the font size
  const avgCharWidth = fontSize * 0.55;
  const optimalChars = 65; // Sweet spot for readability

  return Math.round(avgCharWidth * optimalChars);
}

/**
 * Check if text should use reduced motion based on accessibility preferences
 */
export async function shouldReduceMotion(): Promise<boolean> {
  try {
    return await AccessibilityInfo.isReduceMotionEnabled();
  } catch {
    return false;
  }
}

/**
 * Get contrast ratio between two colors (simplified version)
 */
export function getContrastRatio(
  foreground: string,
  background: string
): number {
  // This is a simplified implementation
  // In a real app, you'd want a more robust color contrast calculation
  const getLuminance = (color: string): number => {
    // Convert hex to RGB and calculate relative luminance
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16) / 255;
    const g = parseInt(hex.substr(2, 2), 16) / 255;
    const b = parseInt(hex.substr(4, 2), 16) / 255;

    const sRGB = [r, g, b].map(c => {
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * sRGB[0] + 0.7152 * sRGB[1] + 0.0722 * sRGB[2];
  };

  const l1 = getLuminance(foreground);
  const l2 = getLuminance(background);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);

  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Validate if text meets WCAG accessibility standards
 */
export function validateTextAccessibility(
  foreground: string,
  background: string,
  fontSize: number,
  fontWeight: string
): {
  isValid: boolean;
  level: 'AA' | 'AAA' | 'fail';
  contrastRatio: number;
} {
  const contrastRatio = getContrastRatio(foreground, background);
  const isLargeText =
    fontSize >= 18 ||
    (fontSize >= 14 && ['bold', '700', '800', '900'].includes(fontWeight));

  let level: 'AA' | 'AAA' | 'fail' = 'fail';
  let isValid = false;

  if (isLargeText) {
    if (contrastRatio >= 4.5) {
      level = 'AAA';
      isValid = true;
    } else if (contrastRatio >= 3) {
      level = 'AA';
      isValid = true;
    }
  } else {
    if (contrastRatio >= 7) {
      level = 'AAA';
      isValid = true;
    } else if (contrastRatio >= 4.5) {
      level = 'AA';
      isValid = true;
    }
  }

  return { isValid, level, contrastRatio };
}
